<template>
  <section class="exclusion-group-table mt-7">
    <h3 class="mb-3 text-base font-extrabold text-slate-950">{{ title }}</h3>

    <div class="overflow-x-auto rounded-md border border-slate-200">
      <div
        class="grid min-w-[720px] grid-cols-[2fr_2.2fr_1fr_0.8fr_44px] items-center gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-extrabold uppercase text-slate-600">
        <span>Group</span>
        <span>Description</span>
        <span>Symbols</span>
        <span>Excluded</span>
        <span></span>
      </div>

      <div v-if="groups.length === 0" class="px-4 py-8 text-center text-slate-500">
        No groups found.
      </div>

      <div v-for="(group, index) in groups" :key="group.id"
        class="grid min-w-[720px] grid-cols-[2fr_2.2fr_1fr_0.8fr_44px] items-center gap-4 border-b border-slate-200 px-4 py-3 last:border-b-0">
        <div class="flex items-center gap-3">
          <span class="inline-flex h-9 w-9 items-center justify-center rounded-md text-white"
            :class="iconTone(index)">
            <i class="fa-solid" :class="iconClass(group)"></i>
          </span>
          <strong class="text-slate-950">{{ formatGroupName(group.name) }}</strong>
        </div>

        <span class="text-slate-600">{{ group.description || '-' }}</span>
        <span class="text-slate-700">{{ (group.symbolCount || 0).toLocaleString() }}</span>

        <!-- Toggle button with per-group spinner -->
        <div class="justify-self-center">
          <!-- Spinner while this group is toggling -->
          <div v-if="togglingId === group.id" class="exclusion-toggle-loading">
            <span class="exclusion-spinner"></span>
          </div>
          <!-- Normal toggle checkbox -->
          <button v-else type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md border p-0 text-white disabled:cursor-not-allowed disabled:opacity-80"
            :class="group.isExcluded ? 'border-violet-700 bg-violet-700' : 'border-slate-300 bg-white'"
            @click="emit('toggle', group)">
            <i v-if="group.isExcluded" class="fa-solid fa-check text-xs"></i>
          </button>
        </div>

        <button type="button"
          class="flex h-9 w-9 items-center justify-center rounded-md bg-transparent p-0 text-slate-600 hover:bg-slate-100 hover:text-violet-700"
          title="View group details" @click="emit('view', group)">
          <i class="fa-solid fa-ellipsis-vertical"></i>
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
/* global defineProps, defineEmits */
defineProps({
  title: {
    type: String,
    required: true,
  },
  groups: {
    type: Array,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  togglingId: {
    type: [Number, String, null],
    default: null,
  },
});

const emit = defineEmits(['view', 'toggle']);

function iconClass(group) {
  const name = String(group.name || '').toLowerCase();
  if (name.includes('monitoring')) return 'fa-circle-exclamation';
  if (name.includes('stock')) return 'fa-chart-simple';
  if (name.includes('commodity')) return 'fa-droplet';
  if (name.includes('stable')) return 'fa-wallet';
  if (name.includes('fan')) return 'fa-users';
  if (name.includes('airdrop')) return 'fa-gift';
  if (name.includes('coin')) return 'fa-star';
  return 'fa-layer-group';
}

function iconTone(index) {
  return [
    'bg-gradient-to-br from-violet-600 to-indigo-600',
    'bg-gradient-to-br from-amber-500 to-orange-500',
    'bg-gradient-to-br from-emerald-500 to-green-600',
    'bg-gradient-to-br from-blue-500 to-blue-700',
    'bg-gradient-to-br from-rose-600 to-rose-700',
  ][index % 5];
}

function formatGroupName(name) {
  if (!name) return '-';
  const clean = String(name).trim().toUpperCase();
  if (clean === 'BSTOCKS' || clean === 'TRADFI_BSTOCKS') return 'TradFi_bStocks';
  if (clean === 'STOCK' || clean === 'TRADFI_FUTURES') return 'TradFi_Futures';
  if (clean === 'TCOMMODITIES' || clean === 'TRADFI_TCOMMODITIES') return 'TradFi_tCommodities';
  return name;
}
</script>

<style>
body.dark-mode .exclusion-group-table h3,
body.dark-mode .exclusion-group-table strong {
  color: #f8fafc;
}

body.dark-mode .exclusion-group-table .border-slate-200 {
  border-color: #363c4e;
}

body.dark-mode .exclusion-group-table .bg-slate-50 {
  background: #111827;
}

body.dark-mode .exclusion-group-table .text-slate-500,
body.dark-mode .exclusion-group-table .text-slate-600,
body.dark-mode .exclusion-group-table .text-slate-700 {
  color: #cbd5e1;
}

body.dark-mode .exclusion-group-table button.bg-white {
  background: #2b3139;
  border-color: #475569;
}

body.dark-mode .exclusion-group-table button.bg-violet-700 {
  background: #6d28d9;
  border-color: #6d28d9;
  color: #ffffff;
}

body.dark-mode .exclusion-group-table button.bg-transparent:hover {
  background: #2b3139;
  color: #c4b5fd;
}
</style>
